/**
 * Growth Metrics Service (2026-08-22) — Phase 2 of the Growth System.
 * Aggregates a barber's REAL POS data (occupancy, rolling-window rebooking, returning, retail,
 * rating, tenure) into GrowthInputs, then runs the pure engine (shared/growth.ts). This is the
 * bridge between live Firestore data and the deterministic score.
 *
 * OCCUPANCY is the net-new metric: available hours = the barber's weekly `availability` projected
 * over the period − blocked time; booked hours = Σ appointment durations (non-cancelled/no-show).
 * REBOOKING uses a ROLLING MATURE WINDOW (not YTD-to-today) so recent-but-not-yet-returned clients
 * don't drag the number. Pure aggregation (takes `now` explicitly) → unit-testable.
 *
 * BEHAVIOR-PIN: SLIK-GROWTH-SYSTEM-BLUEPRINT.md §Phase 2 · shared/growth.ts is the math.
 */
import {
  computeGrowth, resolveStageFromTenure, feedBusinessPillar, growthNudges, currentPayWeekStart,
  mergeCommissionPay, DEFAULT_COMMISSION_PAY, mergeGrowthConfig,
  type GrowthInputs, type GrowthResult, type GrowthConfig, type GrowthMetricKey, type CommissionPayConfig,
  DEFAULT_GROWTH_CONFIG,
} from '../shared/growth';
import { shopSettingsService } from './shopSettingsService';
import { isDayEnabled } from '../shared/availability';
import { appointmentService, type Appointment } from './appointmentService';
import { barberDataService, type Barber, type DayAvailability, type BarberGrowthSnapshot } from './barberDataService';
import { loadClientHistory, barberRetentionById, barberRebookingById, barberTenureWeeksById, type ClientRecord } from './clientHistoryService';

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
const MS_DAY = 86400000;
const NON_BOOKED = new Set(['cancelled', 'no-show']);

function toMinutes(hhmm?: string): number | null {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** Working minutes for one DayAvailability slot (respects enabled dual-schema + break). */
function dayWorkingMinutes(d: DayAvailability | undefined): number {
  if (!d) return 0;
  const enabled = isDayEnabled(d as any); // enabled ?? available ?? true
  if (!enabled) return 0;
  const start = toMinutes(d.start);
  const end = toMinutes(d.end);
  if (start == null || end == null || end <= start) return 0;
  let mins = end - start;
  const bs = toMinutes(d.breakStart);
  const be = toMinutes(d.breakEnd);
  if (bs != null && be != null && be > bs) mins -= (be - bs);
  return Math.max(0, mins);
}

function parseYMD(ymd: string): Date | null {
  if (!ymd || typeof ymd !== 'string') return null;
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export interface GrowthPeriodOpts {
  periodStart: Date;
  periodEnd: Date;
  now: Date;
  /** Blocked hours in the period (time off / blocks) to subtract from available. Default 0. */
  blockedHoursInPeriod?: number;
  /** Persisted stage from prior compute (else resolved from tenure). */
  stage?: 1 | 2 | 3 | 4;
  provisional?: boolean;
  config?: GrowthConfig;
  /** Overrides from the REAL client-history spine (appointments.clientId is only ~2% linked). */
  returningPctOverride?: number;
  rebookingPctOverride?: number;
  tenureWeeksOverride?: number;
}

/**
 * Pure aggregation → GrowthInputs. `allApptsForBarber` should include history back at least
 * ~120 days before periodStart so the rolling rebooking + returning lookbacks work.
 */
export function computeGrowthInputs(
  barber: Barber,
  allApptsForBarber: Appointment[],
  opts: GrowthPeriodOpts,
): GrowthInputs {
  const { periodStart, periodEnd, now } = opts;
  const startMs = periodStart.getTime();
  const endMs = periodEnd.getTime();
  const inPeriod = (a: Appointment) => { const d = parseYMD(a.date); return !!d && d.getTime() >= startMs && d.getTime() <= endMs; };

  const periodAppts = allApptsForBarber.filter(inPeriod);
  const booked = periodAppts.filter(a => !NON_BOOKED.has(a.status));
  const completed = periodAppts.filter(a => a.status === 'completed' || a.status === 'paid');

  // ── Occupancy ──────────────────────────────────────────────────────────────────────
  // Available hours: sum each date's working minutes from the barber's weekly availability.
  let availableMinutes = 0;
  let daysIterated = 0;
  for (let t = startMs; t <= endMs; t += MS_DAY) {
    const dayKey = DAY_KEYS[new Date(t).getDay()];
    availableMinutes += dayWorkingMinutes(barber.availability?.[dayKey]);
    daysIterated++;
  }
  let availableHours = availableMinutes / 60 - (opts.blockedHoursInPeriod || 0);
  availableHours = Math.max(0, availableHours);
  const bookedHours = booked.reduce((s, a) => s + (a.actualDuration || a.duration || 30) / 60, 0);
  const occupancyPct = availableHours > 0 ? Math.min(100, (bookedHours / availableHours) * 100) : 0;
  // Weeks derived from the days actually iterated (internally consistent; Mon–Sun inclusive = 7 days = 1 week).
  const weeks = Math.max(1 / 7, daysIterated / 7);
  const availableHoursPerWeek = availableHours / weeks;

  // ── Returning (same barber) ──────────────────────────────────────────────────────────
  // Of distinct clients seen in the period, what % have >1 lifetime visit to THIS barber.
  const lifetimeVisitsByClient = new Map<string, number>();
  for (const a of allApptsForBarber) {
    if (NON_BOOKED.has(a.status)) continue;
    const cid = a.clientId; if (!cid) continue;
    lifetimeVisitsByClient.set(cid, (lifetimeVisitsByClient.get(cid) || 0) + 1);
  }
  const periodClients = new Set(booked.map(a => a.clientId).filter(Boolean) as string[]);
  let returningCount = 0;
  periodClients.forEach(cid => { if ((lifetimeVisitsByClient.get(cid) || 0) > 1) returningCount++; });
  const returningPct = opts.returningPctOverride != null ? opts.returningPctOverride
    : (periodClients.size > 0 ? (returningCount / periodClients.size) * 100 : 0);

  // ── Rebooking (ROLLING MATURE WINDOW) ────────────────────────────────────────────────
  // Clients with a completed appt in [now-90d, now-30d]: did they have a LATER appt with this
  // barber within 45 days? % yes. Mature window avoids penalizing not-yet-returned recent clients.
  const matureStart = now.getTime() - 90 * MS_DAY;
  const matureEnd = now.getTime() - 30 * MS_DAY;
  const apptTime = (a: Appointment) => parseYMD(a.date)?.getTime() ?? 0;
  const matureAnchors = allApptsForBarber
    .filter(a => (a.status === 'completed' || a.status === 'paid'))
    .filter(a => { const t = apptTime(a); return t >= matureStart && t <= matureEnd; });
  let rebookedYes = 0, rebookDenom = 0;
  const seenAnchorClients = new Set<string>();
  for (const anchor of matureAnchors) {
    const cid = anchor.clientId; if (!cid || seenAnchorClients.has(cid)) continue;
    seenAnchorClients.add(cid);
    rebookDenom++;
    const anchorT = apptTime(anchor);
    const hasLater = allApptsForBarber.some(a =>
      a.clientId === cid && !NON_BOOKED.has(a.status) && apptTime(a) > anchorT && apptTime(a) <= anchorT + 45 * MS_DAY);
    if (hasLater) rebookedYes++;
  }
  const rebookingPct = opts.rebookingPctOverride != null ? opts.rebookingPctOverride
    : (rebookDenom > 0 ? (rebookedYes / rebookDenom) * 100 : 0);

  // ── Retail (% of service revenue) ────────────────────────────────────────────────────
  const serviceRevenue = completed.reduce((s, a) => s + (a.price || 0), 0);
  const productRevenue = completed.reduce((s, a) => {
    const arr = (a as any).productsSold as Array<{ total?: number }> | undefined;
    return s + (Array.isArray(arr) ? arr.reduce((p, x) => p + (x?.total || 0), 0) : 0);
  }, 0);
  const retailPct = serviceRevenue > 0 ? (productRevenue / serviceRevenue) * 100 : 0;

  // ── Rating ───────────────────────────────────────────────────────────────────────────
  const rating = barber.avgRating || 0;
  // Rating only counts when there are REAL reviews. avgRating defaults to a PLACEHOLDER 5, and proxying
  // reviewCount by visit count made every barber score a phantom 100/100 rating (inflating score → tier →
  // BONUS). Until reviews are wired, reviewCount 0 < minReviewsForRating drops rating (weights renormalize).
  // BEHAVIOR-PIN: plausible-but-wrong audit (Jose 2026-09-10).
  const reviewCount = (barber as any).reviewCount ?? 0;

  // ── Tenure ─────────────────────────────────────────────────────────────────────────
  // joinDate is a Firestore Timestamp ({_seconds}/{seconds}/.toDate) — `new Date(timestampObject)` yields
  // Invalid Date → tenure fell back to a wrong stage. Parse it properly. BEHAVIOR-PIN: plausible-but-wrong audit.
  const jd: any = barber.joinDate;
  const join = jd
    ? (typeof jd?.toDate === 'function' ? jd.toDate()
      : typeof jd?._seconds === 'number' ? new Date(jd._seconds * 1000)
      : typeof jd?.seconds === 'number' ? new Date(jd.seconds * 1000)
      : new Date(jd))
    : null;
  const tenureWeeks = opts.tenureWeeksOverride != null ? opts.tenureWeeksOverride
    : (join && !Number.isNaN(join.getTime())
      ? Math.max(0, (now.getTime() - join.getTime()) / (MS_DAY * 7))
      : 260); // missing joinDate → treat as established (avoids mis-ramping veterans)

  const weeklyServiceRevenue = serviceRevenue / weeks;

  return {
    occupancyPct, rebookingPct, returningPct, retailPct, rating, reviewCount,
    availableHoursPerWeek, tenureWeeks, weeklyServiceRevenue,
    weeklyRetailDollars: productRevenue / weeks,
    stage: opts.stage, provisional: opts.provisional,
  };
}

/** Full result for one barber over a period. */
export function computeBarberGrowth(
  barber: Barber,
  allApptsForBarber: Appointment[],
  opts: GrowthPeriodOpts,
): { inputs: GrowthInputs; result: GrowthResult } {
  const inputs = computeGrowthInputs(barber, allApptsForBarber, opts);
  const result = computeGrowth(inputs, opts.config || DEFAULT_GROWTH_CONFIG);
  return { inputs, result };
}

/** Convenience: standard trailing-N-weeks window ending at `now` (default 4 weeks, the rolling window). */
export function trailingWindow(now: Date, weeks: number = 4): { periodStart: Date; periodEnd: Date; now: Date } {
  return { periodStart: new Date(now.getTime() - weeks * 7 * MS_DAY), periodEnd: now, now };
}

// ── Persistence (Phase 3) ──────────────────────────────────────────────────────────────
/** Map the engine result → the persisted per-barber snapshot (pure, testable). */
export function growthResultToSnapshot(result: GrowthResult, nowISO: string): BarberGrowthSnapshot {
  const comp = (k: GrowthMetricKey) => {
    const c = result.components.find(x => x.key === k);
    return c && c.active && c.componentScore >= 0 ? c.componentScore : 0;
  };
  return {
    score: result.score, stage: result.stage, stageName: result.stageName, tier: result.tier,
    provisional: result.provisional, bonusEligible: result.bonus.eligible, bonusPct: result.bonus.tierPct,
    bonusEstWeekly: result.bonus.estWeekly, occupancyTarget: result.occupancyTargetThisStage,
    components: {
      occupancy: comp('occupancy'), rebooking: comp('rebooking'), returning: comp('returning'),
      retail: comp('retail'), rating: comp('rating'),
    },
    weakest: result.weakest?.key ?? null,
    shouldPromote: result.advancement.shouldPromote, advancementReason: result.advancement.reason,
    priceIncreaseEligible: result.priceIncreaseEligibleThisPeriod,
    updatedAt: nowISO,
  };
}

/**
 * Effective stage = never regress: the higher of the tenure-floor stage and any persisted stage.
 * (Tenure advances stages automatically; mastery-acceleration bumps the persisted stage — surfaced
 * via shouldPromote and applied by promoteBarberStage — so this only ever moves forward.)
 */
export function effectiveStage(tenureWeeks: number, persistedStage: number | undefined, config: GrowthConfig): 1 | 2 | 3 | 4 {
  const floor = resolveStageFromTenure(tenureWeeks, config);
  return Math.max(floor, persistedStage || 1) as 1 | 2 | 3 | 4;
}

/** Recompute + persist the growth snapshot for one barber over the rolling window. */
export async function recomputeAndPersistBarber(
  barber: Barber,
  allApptsForBarber: Appointment[],
  now: Date = new Date(),
  config: GrowthConfig = DEFAULT_GROWTH_CONFIG,
  commissionPay: CommissionPayConfig = DEFAULT_COMMISSION_PAY,
  clientRoster?: ClientRecord[],
): Promise<BarberGrowthSnapshot> {
  const win = trailingWindow(now, 4);
  // Override returning/rebooking/tenure from the REAL client-history spine (appointments.clientId is ~2%
  // linked → garbage). BEHAVIOR-PIN: plausible-but-wrong audit (Jose 2026-09-10).
  const roster = clientRoster || [];
  const nowMs = now.getTime();
  const startISO = new Date(nowMs - 90 * MS_DAY).toISOString().slice(0, 10);
  const endISO = new Date(nowMs).toISOString().slice(0, 10);
  const retOv = roster.length ? barberRetentionById(roster, barber.id, startISO, endISO).retentionPct : null;
  const rebOv = roster.length ? barberRebookingById(roster, barber.id, nowMs) : null;
  const tenOv = roster.length ? barberTenureWeeksById(roster, barber.id, nowMs) : null;
  const baseInputs = computeGrowthInputs(barber, allApptsForBarber, {
    ...win, config,
    ...(retOv != null ? { returningPctOverride: retOv } : {}),
    ...(rebOv != null ? { rebookingPctOverride: rebOv } : {}),
    ...(tenOv != null ? { tenureWeeksOverride: tenOv } : {}),
  });
  const stage = effectiveStage(baseInputs.tenureWeeks, barber.growth?.stage, config);
  const result = computeGrowth({ ...baseInputs, stage, provisional: barber.growth?.provisional }, config);
  const snapshot = growthResultToSnapshot(result, now.toISOString());
  // Display fields for the coaching + finance surfaces (computed here where we have the full result + $).
  snapshot.nudges = growthNudges(result, baseInputs.weeklyServiceRevenue);
  snapshot.weeklyServiceRevenue = Math.round(baseInputs.weeklyServiceRevenue * 100) / 100;
  snapshot.weeklyRetailDollars = Math.round((baseInputs.weeklyRetailDollars || 0) * 100) / 100;
  snapshot.occupancyPct = Math.round(baseInputs.occupancyPct * 10) / 10;
  snapshot.availableHoursPerWeek = Math.round(baseInputs.availableHoursPerWeek * 10) / 10;
  // This-pay-week service revenue-to-date → feeds the live commission-cliff projection card.
  // Uses the shop's configured pay-week start + tiers (Settings → Commission & Pay).
  const pwStartISO = currentPayWeekStart(now, commissionPay.payWeekStartDay).toISOString().slice(0, 10);
  const payWeekRevenue = allApptsForBarber
    .filter(a => (a.status === 'completed' || a.status === 'paid') && (a.date || '') >= pwStartISO)
    .reduce((s, a) => s + (a.price || 0), 0);
  snapshot.payWeekRevenue = Math.round(payWeekRevenue * 100) / 100;
  snapshot.payWeekStart = pwStartISO;
  snapshot.commissionTiers = commissionPay.tiers;
  snapshot.retailSpiffPct = commissionPay.retailSpiffPct;

  // Phase 4: auto-feed ONLY the PPI `business` pillar from the Growth outcome metrics, so the existing
  // coaching engine (shared/coach.ts) gets real data instead of manual entry. We deliberately do NOT
  // touch `retention`/`technical`/`professionalHabits`/`attendance`/`communication` — the emergency-booking
  // gate reads those, and must remain coach-entered/unchanged. business pillar is weight-10 and NOT in the gate.
  const existingPpi = (barber as any).ppiScores || {};
  const businessPillar = feedBusinessPillar(result);
  await barberDataService.updateBarber(barber.id, {
    growth: snapshot,
    ppiScores: { ...existingPpi, business: businessPillar },
  } as any);
  return snapshot;
}

/**
 * "Turn it on" orchestration — pulls the LIVE barbers + their appointment history from the in-memory
 * service caches and recomputes/persists every barber's Growth snapshot in one call (owner-triggered
 * button or a nightly Cloud Function). Reads getAllBarbers() + getAppointmentsByBarber() (both include
 * full history for the rolling-window metrics).
 */
export async function recomputeAllFromLiveData(
  now: Date = new Date(),
  config?: GrowthConfig,
): Promise<Array<{ barberId: string; snapshot: BarberGrowthSnapshot }>> {
  const barbers = barberDataService.getAllBarbers();
  // H19–H22 FIX (Jose 2026-10-29 audit): getAppointmentsByBarber() reads the in-memory live cache, which
  // only holds [now-21d, +∞) — but computeGrowthInputs' rebooking/returning windows need ≥120d of history
  // (clients completed in [now-90d, now-30d] who rebooked within 45d). Reading the 21-day cache silently
  // computed an artificially LOW rebooking pct → understated growth stage/tier (pay-tier adjacent). Pull
  // the full window on demand FIRST (ensureDateRange extends the cache back + de-dupes; no-op if covered).
  const back = new Date(now);
  back.setDate(back.getDate() - 130); // a little past 120d so the rebooking anchor window is fully covered
  const backYmd = `${back.getFullYear()}-${String(back.getMonth() + 1).padStart(2, '0')}-${String(back.getDate()).padStart(2, '0')}`;
  try { await appointmentService.ensureDateRange(backYmd); } catch { /* non-fatal — metrics compute on what's cached */ }
  const apptsByBarberId: Record<string, Appointment[]> = {};
  for (const b of barbers) apptsByBarberId[b.id] = appointmentService.getAppointmentsByBarber(b.id);
  // Load the shop's Growth & Bonus config (weights/targets/tiers/bonus %) so the recompute uses the
  // owner's tuned parameters, not the code defaults (Settings → Growth & Bonus). Jose 2026-09-27.
  let growthConfig: GrowthConfig = config || DEFAULT_GROWTH_CONFIG;
  if (!config) {
    try { growthConfig = mergeGrowthConfig(await shopSettingsService.getSettingsByType('growthConfig')); }
    catch { /* fall back to defaults */ }
  }
  // Load the shop's Commission & Pay config (pay-week start + tiers) so the recompute uses it.
  let commissionPay: CommissionPayConfig = DEFAULT_COMMISSION_PAY;
  try { commissionPay = mergeCommissionPay(await shopSettingsService.getSettingsByType('commissionPay')); }
  catch { /* fall back to defaults */ }
  // Real client-history spine → correct returning/rebooking/tenure (not the ~2%-linked appointments).
  let clientRoster: ClientRecord[] = [];
  try { clientRoster = await loadClientHistory(); } catch { /* empty → falls back to appt-derived */ }
  return recomputeAndPersistAll(barbers, apptsByBarberId, now, growthConfig, commissionPay, clientRoster);
}

/** Recompute + persist for all barbers. `apptsByBarberId` should include ≥120d history per barber. */
export async function recomputeAndPersistAll(
  barbers: Barber[],
  apptsByBarberId: Record<string, Appointment[]>,
  now: Date = new Date(),
  config: GrowthConfig = DEFAULT_GROWTH_CONFIG,
  commissionPay: CommissionPayConfig = DEFAULT_COMMISSION_PAY,
  clientRoster?: ClientRecord[],
): Promise<Array<{ barberId: string; snapshot: BarberGrowthSnapshot }>> {
  const out: Array<{ barberId: string; snapshot: BarberGrowthSnapshot }> = [];
  for (const b of barbers) {
    try {
      const snap = await recomputeAndPersistBarber(b, apptsByBarberId[b.id] || [], now, config, commissionPay, clientRoster);
      out.push({ barberId: b.id, snapshot: snap });
    } catch (e) {
      console.error('[growthMetrics] recompute failed for', b.id, e);
    }
  }
  return out;
}
